import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import axiosInstance from '../utils/axiosInstance';
import AuthService from '../services/authService';

const Wrapper = styled.div`
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 20px;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: #6b7280;
  font-size: 14px;
`;

const ClassCard = styled.div`
  border: 1px solid #c7d2fe;
  background: #eef2ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const ClassName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #312e81;
`;

const ClassMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #4338ca;
`;

const StatusCard = styled.div`
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  border: 1px solid ${(p) => p.$tone === 'error' ? '#fecaca' : p.$tone === 'success' ? '#bbf7d0' : p.$tone === 'warning' ? '#fde68a' : '#e5e7eb'};
  background: ${(p) => p.$tone === 'error' ? '#fef2f2' : p.$tone === 'success' ? '#f0fdf4' : p.$tone === 'warning' ? '#fffbeb' : '#f9fafb'};
`;

const StatusIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.$tone === 'error' ? '#991b1b' : p.$tone === 'success' ? '#166534' : p.$tone === 'warning' ? '#92400e' : '#111827'};
`;

const StatusText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${(p) => p.$tone === 'error' ? '#b91c1c' : p.$tone === 'success' ? '#15803d' : p.$tone === 'warning' ? '#a16207' : '#6b7280'};
`;

const ScanButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background: ${(p) => (p.disabled ? '#a5b4fc' : '#4f46e5')};
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.disabled ? '#a5b4fc' : '#4338ca')};
  }
`;

const Hint = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
`;

const DeniedScreen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f9fafb;
`;

const DeniedCard = styled.div`
  max-width: 380px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
`;

const DeniedIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const DeniedTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  color: #111827;
`;

const DeniedText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const DeniedLoginLink = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  font-size: 13px;
  color: #4f46e5;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

// Reached whenever this URL is opened without an active MYC session -- e.g.
// pasted into a browser/tab that isn't the student's own logged-in phone.
// Deliberately does NOT redirect to /login: this link only works when
// scanned with the camera from inside the student's own MYC session, so a
// silent redirect there would just be confusing (or invite logging in as
// someone else to scan on a different student's behalf).
function CannotAccessDirectly() {
  return (
    <DeniedScreen>
      <DeniedCard>
        <DeniedIcon>🚫</DeniedIcon>
        <DeniedTitle>You can't access this directly</DeniedTitle>
        <DeniedText>
          This link only works when scanned with your phone's camera from your own MYC session.
          Please open MYC and use your camera to scan the QR code shown by your lecturer.
        </DeniedText>
        <DeniedLoginLink to="/login">Go to MYC login</DeniedLoginLink>
      </DeniedCard>
    </DeniedScreen>
  );
}

function formatTimeRange(start, end) {
  const to12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    let hours = parseInt(h, 10);
    const modifier = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${m} ${modifier}`;
  };
  return `${to12h(start)} - ${to12h(end)}`;
}

// Maps navigator.geolocation error codes to a message a student can actually
// act on -- "enable location" vs "try again" are different fixes.
function describeGeolocationError(error) {
  if (!error) return 'Could not get your location. Please try again.';
  switch (error.code) {
    case 1: // PERMISSION_DENIED
      return 'Location access was denied. Please enable Location Services for your browser and try again.';
    case 2: // POSITION_UNAVAILABLE
      return "Couldn't determine your location. Please check that GPS/location is turned on for your device.";
    case 3: // TIMEOUT
      return 'Getting your location took too long. Please try again.';
    default:
      return 'Could not get your location. Please try again.';
  }
}

export default function ScanAttendance() {
  const { token } = useParams();
  const authData = AuthService.getUser();
  const hasSession = Boolean(authData && authData.apiKey && authData.studentId);
  // The QR is scoped to one class's batch -- it must match whichever course
  // the student currently has selected in MYC, not just any batch they
  // happen to also be registered in.
  const selectedBatchId = authData?.selectedRegistration?.batchId || null;

  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loadError, setLoadError] = useState(null); // { tone, title, text }
  const [scanState, setScanState] = useState('idle'); // idle | locating | submitting | done
  const [scanError, setScanError] = useState(null);
  const [scanResult, setScanResult] = useState(null); // { status } after success/duplicate

  useEffect(() => {
    if (!hasSession) {
      setLoading(false);
      return undefined;
    }
    if (!selectedBatchId) {
      setLoading(false);
      setLoadError({ tone: 'error', title: 'No course selected', text: 'Please select your course in MYC first, then scan this QR code again.' });
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    axiosInstance.get(`/students/qr-attendance/sessions/${token}`, { params: { selectedBatchId } })
      .then((res) => {
        if (cancelled) return;
        setSessionInfo(res.data);
        // The real attendance record (if it already exists, from this QR
        // session or an earlier one, or a normal manual mark) is the
        // authoritative outcome -- takes priority over a merely-pending scan.
        if (res.data.attendanceStatus) {
          setScanResult({ attendanceStatus: res.data.attendanceStatus });
        } else if (res.data.alreadyScanned) {
          setScanResult({ status: res.data.scanStatus });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        if (status === 410) {
          setLoadError({ tone: 'warning', title: 'QR code expired', text: message || 'This QR code is no longer active. Ask your lecturer to generate a new one.' });
        } else if (status === 422 && err?.response?.data?.invalidBatch) {
          setLoadError({ tone: 'error', title: 'Invalid QR scan', text: message || 'This QR code is for a different batch. Please select the correct course in MYC and scan again.' });
        } else if (status === 403) {
          setLoadError({ tone: 'error', title: 'Not registered for this class', text: message || 'You are not registered for this class.' });
        } else if (status === 404) {
          setLoadError({ tone: 'error', title: 'Invalid QR code', text: message || 'This QR code is invalid.' });
        } else {
          setLoadError({ tone: 'error', title: 'Something went wrong', text: message || 'Failed to load this QR code. Please try again.' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, hasSession, selectedBatchId]);

  if (!hasSession) {
    return <CannotAccessDirectly />;
  }

  const handleScan = () => {
    if (!navigator.geolocation) {
      setScanError('Your browser does not support location services, which are required to mark attendance.');
      return;
    }

    setScanError(null);
    setScanState('locating');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setScanState('submitting');
        axiosInstance.post(`/students/qr-attendance/sessions/${token}/scan`, { latitude, longitude, accuracy, selectedBatchId })
          .then(() => {
            setScanResult({ status: 'pending' });
            setScanState('done');
          })
          .catch((err) => {
            const status = err?.response?.status;
            if (status === 409 && err?.response?.data?.attendanceStatus) {
              setScanResult({ attendanceStatus: err.response.data.attendanceStatus });
              setScanState('done');
              return;
            }
            if (status === 409) {
              setScanResult({ status: err?.response?.data?.status || 'pending' });
              setScanState('done');
              return;
            }
            setScanError(err?.response?.data?.message || 'Failed to record your attendance scan. Please try again.');
            setScanState('idle');
          });
      },
      (error) => {
        setScanError(describeGeolocationError(error));
        setScanState('idle');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  return (
    <StudentLayout title="Scan Attendance">
      <Wrapper>
        <Section>
          <Title>QR Attendance Scan</Title>
          <Subtitle>Scan recorded here is sent to your lecturer for confirmation before it counts as attendance.</Subtitle>

          {loading && <Loader message="Checking QR code…" />}

          {!loading && loadError && (
            <StatusCard $tone={loadError.tone}>
              <StatusIcon>{loadError.tone === 'warning' ? '⏳' : '⚠️'}</StatusIcon>
              <StatusTitle $tone={loadError.tone}>{loadError.title}</StatusTitle>
              <StatusText $tone={loadError.tone}>{loadError.text}</StatusText>
            </StatusCard>
          )}

          {!loading && !loadError && sessionInfo && (
            <>
              <ClassCard>
                <ClassName>{sessionInfo.subjectName}</ClassName>
                <ClassMeta>{sessionInfo.batchLabel} {sessionInfo.courseName ? `· ${sessionInfo.courseName}` : ''}</ClassMeta>
                <ClassMeta>{sessionInfo.date} · {formatTimeRange(sessionInfo.startTime, sessionInfo.endTime)}</ClassMeta>
              </ClassCard>

              {scanResult?.attendanceStatus ? (
                // The real attendance record already exists (from this QR
                // session, an earlier one, or a normal manual mark) -- this
                // is the final, authoritative outcome, not just a pending scan.
                <StatusCard $tone={scanResult.attendanceStatus === 'Present' ? 'success' : scanResult.attendanceStatus === 'Absent' ? 'error' : 'warning'}>
                  <StatusIcon>{scanResult.attendanceStatus === 'Present' ? '✅' : scanResult.attendanceStatus === 'Absent' ? '❌' : '📌'}</StatusIcon>
                  <StatusTitle $tone={scanResult.attendanceStatus === 'Present' ? 'success' : scanResult.attendanceStatus === 'Absent' ? 'error' : 'warning'}>
                    Already marked {scanResult.attendanceStatus}
                  </StatusTitle>
                  <StatusText $tone={scanResult.attendanceStatus === 'Present' ? 'success' : scanResult.attendanceStatus === 'Absent' ? 'error' : 'warning'}>
                    Your attendance for this class is already recorded as {scanResult.attendanceStatus}.
                  </StatusText>
                </StatusCard>
              ) : scanResult ? (
                <StatusCard $tone={scanResult.status === 'confirmed' ? 'success' : scanResult.status === 'rejected' ? 'error' : 'warning'}>
                  <StatusIcon>
                    {scanResult.status === 'confirmed' ? '✅' : scanResult.status === 'rejected' ? '❌' : '🕒'}
                  </StatusIcon>
                  <StatusTitle $tone={scanResult.status === 'confirmed' ? 'success' : scanResult.status === 'rejected' ? 'error' : 'warning'}>
                    {scanResult.status === 'confirmed' && 'Attendance confirmed'}
                    {scanResult.status === 'rejected' && 'Scan was rejected'}
                    {(!scanResult.status || scanResult.status === 'pending') && 'Scan recorded'}
                  </StatusTitle>
                  <StatusText $tone={scanResult.status === 'confirmed' ? 'success' : scanResult.status === 'rejected' ? 'error' : 'warning'}>
                    {scanResult.status === 'confirmed' && 'Your lecturer has confirmed your attendance for this class.'}
                    {scanResult.status === 'rejected' && 'Your lecturer rejected this scan. Please speak to them directly.'}
                    {(!scanResult.status || scanResult.status === 'pending') && 'Waiting for your lecturer to confirm this scan.'}
                  </StatusText>
                </StatusCard>
              ) : (
                <>
                  <ScanButton
                    type="button"
                    onClick={handleScan}
                    disabled={scanState === 'locating' || scanState === 'submitting'}
                  >
                    {scanState === 'locating' && 'Getting your location…'}
                    {scanState === 'submitting' && 'Submitting…'}
                    {scanState === 'idle' && 'Mark My Attendance'}
                  </ScanButton>
                  <Hint>Make sure Location Services is turned on before scanning.</Hint>
                  {scanError && (
                    <StatusCard $tone="error" style={{ marginTop: 16 }}>
                      <StatusText $tone="error">{scanError}</StatusText>
                    </StatusCard>
                  )}
                </>
              )}
            </>
          )}
        </Section>
      </Wrapper>
    </StudentLayout>
  );
}
